import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Icon from 'components/AppIcon';
import Image from 'components/AppImage';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';

const ApplicationTracker = ({ onRefresh }) => {
  const { user } = useAuth();
  const [filter, setFilter] = useState('all');
  const [applications, setApplications] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  const [pendingUpdates, setPendingUpdates] = useState({});

  useEffect(() => {
    if (!user) return;

    const fetchApplications = async () => {
      setIsLoading(true);
      try {
        const { data, error } = await supabase
          .from('job_applications')
          .select(`
            *,
            job:jobs(
              *,
              company:companies(*)
            )
          `)
          .eq('user_id', user.id)
          .order('applied_at', { ascending: false });

        if (error) throw error;

        // Transform data
        const transformed = data.map(app => ({
          id: app.id,
          jobId: app.job?.id,
          jobTitle: app.job?.title,
          company: app.job?.company?.name,
          companyLogo: app.job?.company?.logo_url,
          location: app.job?.location,
          appliedDate: app.applied_at,
          status: app.status,
          statusText: app.status.charAt(0).toUpperCase() + app.status.slice(1),
          lastUpdated: 'Recently',
          timeline: [
            { date: app.applied_at, status: 'applied', text: 'Application Submitted' }
          ]
        }));

        setApplications(transformed);
      } catch (err) {
        console.error('Error fetching applications:', err.message);
      } finally {
        setIsLoading(false);
      }
    };

    fetchApplications();
  }, [user]);

  const handleStatusChange = (applicationId, newStatus) => {
    setPendingUpdates(prev => ({
      ...prev,
      [applicationId]: newStatus
    }));
  };

  const saveStatusChange = async (applicationId) => {
    const newStatus = pendingUpdates[applicationId];
    if (!newStatus) return;

    await handleStatusUpdate(applicationId, newStatus);

    // Clear pending state for this item
    setPendingUpdates(prev => {
      const next = { ...prev };
      delete next[applicationId];
      return next;
    });
  };

  const handleStatusUpdate = async (applicationId, newStatus) => {
    try {
      const { error } = await supabase
        .from('job_applications')
        .update({ status: newStatus })
        .eq('id', applicationId);

      if (error) throw error;

      // Update local state
      setApplications(prev => prev.map(app =>
        app.id === applicationId
          ? { ...app, status: newStatus, statusText: getStatusLabel(newStatus) }
          : app
      ));

      // Refresh parent dashboard metrics if callback provided
      if (onRefresh) onRefresh();

    } catch (err) {
      console.error('Error updating status:', err.message);
      alert('Failed to update status');
    }
  };

  const filteredApplications = filter === 'all'
    ? applications
    : applications.filter(app => {
      // Use the current saved status for filtering, not the pending one
      // so items don't jump lists while editing
      if (filter === 'applied') return ['pending', 'submitted'].includes(app.status);
      if (filter === 'interview') return app.status === 'interviewing';
      if (filter === 'offer') return app.status === 'offered';
      return app.status === filter;
    });

  const getStatusLabel = (status) => {
    const labels = {
      'pending': 'Applied',
      'submitted': 'Applied',
      'interviewing': 'Interview',
      'offered': 'Offer',
      'rejected': 'Rejected'
    };
    return labels[status] || status;
  };

  const getStatusInfo = (status) => {
    const statusMap = {
      pending: { icon: 'Send', color: 'text-primary-600', bgColor: 'bg-primary-50' },
      submitted: { icon: 'Send', color: 'text-primary-600', bgColor: 'bg-primary-50' },
      interviewing: { icon: 'Calendar', color: 'text-success-600', bgColor: 'bg-success-50' },
      rejected: { icon: 'XCircle', color: 'text-error-600', bgColor: 'bg-error-50' },
      offered: { icon: 'Award', color: 'text-warning-600', bgColor: 'bg-warning-50' }
    };

    return statusMap[status] || statusMap.pending;
  };

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'short', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('en-US', options);
  };

  return (
    <div>
      {/* Filter Tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {[
          { id: 'all', label: 'All Applications' },
          { id: 'applied', label: 'Applied' },
          { id: 'interview', label: 'Interview' },
          { id: 'offer', label: 'Offer' },
          { id: 'rejected', label: 'Rejected' }
        ].map((tab) => (
          <button
            key={tab.id}
            onClick={() => setFilter(tab.id)}
            className={`px-4 py-2 rounded-full text-sm font-medium transition-smooth ${filter === tab.id
              ? 'bg-primary-50 text-primary-600' : 'bg-surface text-text-secondary hover:bg-secondary-100'
              }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* Applications List */}
      {filteredApplications.length > 0 ? (
        <div className="space-y-4">
          {filteredApplications.map((application) => {
            // Determine active status: pending specific override OR current db status
            const currentStatus = pendingUpdates[application.id] || application.status;

            // Normalize for UI logic if needed, but getStatusInfo handles raw db codes
            const statusInfo = getStatusInfo(currentStatus);

            const hasPendingChange = pendingUpdates[application.id] && pendingUpdates[application.id] !== application.status;

            return (
              <div
                key={application.id}
                className="bg-card text-card-foreground rounded-2xl shadow-lg overflow-hidden"
              >
                {/* Application Header */}
                <div className="p-4 sm:p-6 border-b border-border">
                  <div className="flex items-start">
                    <div className="w-12 h-12 rounded-lg overflow-hidden flex-shrink-0 mr-4">
                      <Image
                        src={application.companyLogo}
                        alt={application.company}
                        className="w-full h-full object-cover"
                      />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <h4 className="text-base font-medium text-text-primary">
                            {application.jobTitle}
                          </h4>
                          <p className="text-sm text-text-secondary mt-1">
                            {application.company} • {application.location}
                          </p>
                        </div>

                        {/* Interactive Status Dropdown */}
                        <div className="mt-2 sm:mt-0 flex items-center gap-2">
                          {hasPendingChange && (
                            <button
                              onClick={() => saveStatusChange(application.id)}
                              className="inline-flex items-center px-3 py-1 rounded-full text-xs font-bold text-white bg-primary hover:bg-primary-dark transition-colors shadow-sm animate-in fade-in"
                            >
                              Save
                            </button>
                          )}

                          <div className={`relative group inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${statusInfo.bgColor} ${statusInfo.color}`}>
                            <Icon name={statusInfo.icon} size={16} className="mr-1" />
                            <select
                              className={`bg-transparent outline-none cursor-pointer appearance-none pr-4 font-semibold ${statusInfo.color}`}
                              value={['submitted', 'pending'].includes(currentStatus) ? 'pending' : currentStatus}
                              onChange={(e) => handleStatusChange(application.id, e.target.value)}
                            >
                              <option value="pending">Applied</option>
                              <option value="interviewing">Interview</option>
                              <option value="offered">Offer</option>
                              <option value="rejected">Rejected</option>
                            </select>
                            <Icon name="ChevronDown" size={12} className="absolute right-2 pointer-events-none opacity-70" />
                          </div>
                        </div>

                      </div>

                      <div className="flex flex-wrap items-center mt-3 text-sm">
                        <div className="flex items-center mr-4 text-text-secondary">
                          <Icon name="Calendar" size={16} className="mr-1" />
                          <span>Applied: {formatDate(application.appliedDate)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Application Actions */}
                <div className="px-4 sm:px-6 py-3 bg-muted/30 flex flex-wrap gap-2">
                  <Link
                    to={`/job-detail-application/${application.jobId}`}
                    className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-primary-600 bg-primary-50 hover:bg-primary-100 transition-smooth"
                  >
                    <Icon name="ExternalLink" size={16} className="mr-1" />
                    <span>View Job</span>
                  </Link>

                  <button className="inline-flex items-center px-3 py-1.5 rounded-md text-sm font-medium text-text-secondary bg-background hover:bg-secondary-100 transition-smooth ml-auto">
                    <Icon name="MoreHorizontal" size={16} />
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      ) : isLoading ? (
        <div className="flex items-center justify-center p-12">
          <Icon name="Loader2" size={32} className="animate-spin text-primary" />
        </div>
      ) : (
        <div className="bg-background border border-border rounded-lg shadow-soft p-6 text-center">
          {/* Empty state simplified */}
          <div className="w-16 h-16 mx-auto mb-4 bg-surface rounded-full flex items-center justify-center">
            <Icon name="FileText" size={24} className="text-secondary-400" />
          </div>
          <h4 className="text-lg font-medium text-text-primary mb-2">No Applications Found</h4>
          <p className="text-sm text-text-secondary mb-4">
            You don't have any applications in this category.
          </p>
          <Link to="/job-search-browse" className="btn-primary inline-flex items-center">
            <Icon name="Search" size={16} className="mr-2" />
            <span>Find Jobs</span>
          </Link>
        </div>
      )}
    </div>
  );
};

export default ApplicationTracker;